/**
 * Bands: extra segments a feature outside the chart wants drawn against a series the chart is
 * already showing.
 *
 * This file is the contract between the chart and whatever is feeding it. The chart imports it to
 * apply bands; the what-if imports it to describe them. Neither imports the other, which is the
 * entire point: the chart must be able to draw a battery band without knowing what a battery is.
 *
 * Read before changing anything here, because two of these rules were got wrong once already and
 * neither failure announces itself:
 *
 * 1. ORDER IS A VALUE DEPENDENCY, NOT A PREFERENCE. Injected bands are applied BEFORE the chart's
 *    own derived bands. The chart splits a negative grid segment out as "Exported to grid", and it
 *    must only ever see the export a battery band did not already take. Reverse the two and the
 *    lookup below fails to match a segment that has already been renamed, so the band silently does
 *    not draw at all, on exactly the buckets it belongs on, while the export figure keeps its full
 *    un-reduced value. A missing band and a wrong number, from a string that stopped matching.
 *
 * 2. A BAND IS EMITTED ON EVERY BAR, ZEROS INCLUDED. ngx-charts assigns ordinal colours by where a
 *    label FIRST appears in the flattened results, so a band that only showed up on the third bar
 *    would shift the colour mapping and hand the series each other's colours.
 */

/** One stacked bar: a date label plus one segment per series. Shared with the chart. */
export interface StackedPoint {
  name: string;
  series: { name: string; value: number }[];
}

/**
 * What a band does to the segment it attaches to.
 *
 * `carve` takes value OUT of the source segment, so the bar total is unchanged and the segment
 * simply splits in two. The site used what it used; a battery only changes where it came from.
 *
 * `add` puts a new segment on top, growing the bar. Units bought to fill a battery really are extra
 * import that would not otherwise have happened, and drawing them any other way hides that.
 */
export type BandMode = 'carve' | 'add';

export interface SeriesBand {
  /** Legend label and stack segment name. Must be unique across the whole stack. */
  name: string;

  /** The band's own colour. The chart builds its ordinal domain from these, in emitted order. */
  rgb: string;

  /** Name of the segment this band attaches to, as it appears in the stack right now. */
  source: string;

  mode: BandMode;

  /**
   * Which side of the source segment the band sits on, reading outward from the axis.
   *
   * `inside` puts it between the axis and what remains of the source, `outside` beyond it. For a
   * carve, inside is nearly always right: what the battery covered reads first, then what still had
   * to be bought. For an add, outside is what makes it legible as something extra rather than
   * something that was always there.
   */
  side: 'inside' | 'outside';

  /**
   * Which sign of the source segment this band applies to.
   *
   * The grid series carries import as positive and export as negative in the SAME series, so the
   * sign is what selects between a band about buying and a band about selling. A band declared
   * `positive` contributes nothing on a bucket that exported, and vice versa.
   *
   * `always` exists for a real case, not for symmetry: units bought overnight to fill a battery are
   * extra import whatever the bucket did on balance. A day that exported overall can still have
   * charged at 02:00, and at day or month granularity that is common rather than exotic. Declaring
   * it `positive` would silently drop those units off the chart.
   *
   * Note this selects whether the band CONTRIBUTES, not whether it is emitted. A band that does not
   * apply is still drawn at zero, per rule 2 at the top of this file.
   */
  appliesWhen: 'positive' | 'negative' | 'always';

  /** One value per bucket, same order and length as the bars. Zeros included, see rule 2 above. */
  valueByBucket: number[];
}

/**
 * Apply bands to the stack, in order, returning new bars.
 *
 * Bands are applied in array order and each one sees the result of the last, so a band may attach to
 * a segment an earlier band created. Nothing is mutated.
 */
export function applyBands(bars: StackedPoint[], bands: SeriesBand[]): StackedPoint[] {
  return bands.reduce((acc, band) => applyBand(acc, band), bars);
}

function applyBand(bars: StackedPoint[], band: SeriesBand): StackedPoint[] {
  return bars.map((bar, i) => {
    const idx = bar.series.findIndex(s => s.name === band.source);
    if (idx < 0) return bar;

    const sourceVal = bar.series[idx].value;
    const wanted = band.valueByBucket[i] ?? 0;

    // The sign of the source picks the band, and it also bounds it. Never claim to have carved more
    // than was there: the battery trace is hourly and a bucket is not, so an hour the battery
    // covered can sit inside a day that went the other way on balance.
    const applies = band.appliesWhen === 'always'
      || (band.appliesWhen === 'positive' && sourceVal > 0)
      || (band.appliesWhen === 'negative' && sourceVal < 0);

    let amount = 0;
    if (applies) {
      amount = band.mode === 'carve' ? Math.min(wanted, Math.abs(sourceVal)) : wanted;
    }

    const series = bar.series.slice();

    // A carve moves value out of the source and into the band, keeping the bar total identical. The
    // band takes the source's sign, so a carve off an exporting bucket draws below the axis with it.
    if (band.mode === 'carve') {
      const signed = sourceVal < 0 ? -amount : amount;
      series[idx] = { ...series[idx], value: sourceVal - signed };
      const at = band.side === 'inside' ? idx : idx + 1;
      series.splice(at, 0, { name: band.name, value: signed });
      return { ...bar, series };
    }

    // An add leaves the source alone and grows the bar.
    const at = band.side === 'inside' ? idx : idx + 1;
    series.splice(at, 0, { name: band.name, value: amount });
    return { ...bar, series };
  });
}
